import { useEffect, useState } from "react";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
const CALENDAR_TIME_ZONE = "Asia/Manila";

// Announcement YYYY-MM-DD fields are calendar days: compare inclusive Manila day keys
// and format the key in UTC so neither the setting nor its end boundary shifts by browser timezone.
function dateToKey(date) {
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CALENDAR_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  const { year, month, day } = values;
  return `${year}-${month}-${day}`;
}

export function toCalendarDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.trim().match(DATE_ONLY_PATTERN);
    if (match) {
      const [, year, month, day] = match;
      const check = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
      if (
        check.getUTCFullYear() === Number(year) &&
        check.getUTCMonth() === Number(month) - 1 &&
        check.getUTCDate() === Number(day)
      ) {
        return `${year}-${month}-${day}`;
      }
      return "";
    }
  }

  const date = typeof value?.toDate === "function"
    ? value.toDate()
    : value instanceof Date
      ? value
      : new Date(value);
  return dateToKey(date);
}

export function calendarDateToLocalDate(value) {
  const key = toCalendarDateKey(value);
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatCalendarDate(value, options = {}) {
  const key = toCalendarDateKey(value);
  if (!key) return "";
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
    ...options
  });
}

export function isCalendarDateInRange(start, end, now = new Date()) {
  const startKey = toCalendarDateKey(start);
  const endKey = toCalendarDateKey(end);
  const todayKey = toCalendarDateKey(now);
  return !!startKey && !!endKey && startKey <= endKey && todayKey >= startKey && todayKey <= endKey;
}

export function buildEnrollmentWindows(data = {}, today = new Date()) {
  const fallbackYear = data.schoolYearStart && data.schoolYearEnd
    ? `${data.schoolYearStart}-${data.schoolYearEnd}`
    : "";
  const windows = [
    {
      key: "first",
      semester: "First Semester",
      year: data.firstSemYearStart && data.firstSemYearEnd
        ? `${data.firstSemYearStart}-${data.firstSemYearEnd}`
        : fallbackYear,
      start: data.firstSemEnrollmentStart || data.firstSemStart || "",
      end: data.firstSemEnrollmentEnd || data.firstSemEnd || ""
    },
    {
      key: "second",
      semester: "Second Semester",
      year: data.secondSemYearStart && data.secondSemYearEnd
        ? `${data.secondSemYearStart}-${data.secondSemYearEnd}`
        : fallbackYear,
      start: data.secondSemEnrollmentStart || data.secondSemStart || "",
      end: data.secondSemEnrollmentEnd || data.secondSemEnd || ""
    }
  ].filter(window => toCalendarDateKey(window.start) && toCalendarDateKey(window.end));
  const todayKey = toCalendarDateKey(today);
  const active = windows.find(window => isCalendarDateInRange(window.start, window.end, todayKey)) || null;
  const next = windows
    .filter(window => todayKey < toCalendarDateKey(window.start))
    .sort((a, b) => toCalendarDateKey(a.start).localeCompare(toCalendarDateKey(b.start)))[0] || null;
  return { active, next };
}

export function getActiveSemester(data = {}, today = new Date()) {
  if (isCalendarDateInRange(data.firstSemStart, data.firstSemEnd, today)) return "first";
  if (isCalendarDateInRange(data.secondSemStart, data.secondSemEnd, today)) return "second";
  return "";
}

export function getAnnouncementSchoolYear(data = {}, semester = "") {
  const prefix = semester === "second" ? "secondSem" : "firstSem";
  const start = data[`${prefix}YearStart`];
  const end = data[`${prefix}YearEnd`];
  if (start && end) return `${start}-${end}`;
  if (data.schoolYear) return data.schoolYear;
  return data.schoolYearStart && data.schoolYearEnd
    ? `${data.schoolYearStart}-${data.schoolYearEnd}`
    : "";
}

export function getCalendarRangeStatus(start, end, now = new Date()) {
  const startKey = toCalendarDateKey(start);
  const endKey = toCalendarDateKey(end);
  const todayKey = toCalendarDateKey(now);
  if (!startKey || !endKey || startKey > endKey) return "Waiting";
  if (todayKey >= startKey && todayKey <= endKey) return "Ongoing";
  return todayKey > endKey ? "Ended" : "Waiting";
}

export function validateCalendarDateRanges(ranges) {
  for (const { label, start, end } of ranges) {
    const startKey = toCalendarDateKey(start);
    const endKey = toCalendarDateKey(end);
    if (!startKey || !endKey) {
      return `${label}: select both a start date and an end date.`;
    }
    if (endKey < startKey) {
      return `${label}: the end date must be on or after the start date.`;
    }
  }
  return "";
}

export function validateCalendarDatesNotInPast(dates, today = new Date()) {
  const todayKey = toCalendarDateKey(today);
  for (const { label, value } of dates) {
    const dateKey = toCalendarDateKey(value);
    if (dateKey && dateKey < todayKey) {
      return `${label}: past dates are not allowed. Select today or a future date.`;
    }
  }
  return "";
}

export function validateConsecutiveSchoolYear(label, start, end) {
  if (!start || !end) {
    return `${label}: select both the start year and end year.`;
  }
  const startYear = Number(start);
  const endYear = Number(end);
  if (!Number.isInteger(startYear) || !Number.isInteger(endYear) || endYear !== startYear + 1) {
    return `${label}: the end year must be one year after the start year.`;
  }
  return "";
}

export function useCalendarClock() {
  const [todayKey, setTodayKey] = useState(() => toCalendarDateKey(new Date()));

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextKey = toCalendarDateKey(new Date());
      setTodayKey(currentKey => currentKey === nextKey ? currentKey : nextKey);
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  return todayKey;
}
